import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, ExternalLink, MapPin, Navigation, Route } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { SHOW_ZELLO_SECTIONS } from '../lib/featureFlags';

const POLL_INTERVAL_MS = 15000;
const ZELLO_PROVIDER = (process.env.REACT_APP_ZELLO_PROVIDER || 'zello-channel').trim() || 'zello-channel';
const ZELLO_BASE_PATH = `/api/zello/${ZELLO_PROVIDER}`;

const formatTimestamp = (value) => {
  if (!value) {
    return 'Unknown time';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown time';
  }

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const toMapEmbedUrl = (lat, lon) => `https://www.google.com/maps?q=${lat},${lon}&z=11&output=embed`;

const PublicLocationsSection = () => {
  const [channelName, setChannelName] = useState('Public Chat');
  const [members, setMembers] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [selectedMember, setSelectedMember] = useState('');
  const [mapFocus, setMapFocus] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!SHOW_ZELLO_SECTIONS) {
      return undefined;
    }

    let isMounted = true;

    const loadLocations = async () => {
      try {
        const response = await apiFetch(`${ZELLO_BASE_PATH}/public-chat/locations?limit=160&historyPerMember=12`);
        if (!response.ok) {
          if (isMounted) {
            setMembers([]);
            setLoadError(true);
          }
          return;
        }

        const payload = await response.json();
        if (!isMounted) {
          return;
        }

        const nextMembers = Array.isArray(payload.members) ? payload.members : [];
        setChannelName(typeof payload.channel === 'string' && payload.channel.trim() ? payload.channel : 'Public Chat');
        setFetchedAt(typeof payload.fetchedAt === 'string' ? payload.fetchedAt : null);
        setMembers(nextMembers);

        if (nextMembers.length > 0) {
          const hasSelected = nextMembers.some((member) => member.member === selectedMember);
          if (!selectedMember || !hasSelected) {
            setSelectedMember(nextMembers[0].member);
          }
        } else {
          setSelectedMember('');
        }
        setLoadError(false);
      } catch {
        if (isMounted) {
          setMembers([]);
          setLoadError(true);
        }
      }
    };

    loadLocations();
    const pollTimer = setInterval(loadLocations, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(pollTimer);
    };
  }, [selectedMember]);

  const activeMember = useMemo(
    () => members.find((member) => member.member === selectedMember) || members[0] || null,
    [members, selectedMember]
  );

  const latestPoint = activeMember?.latest || null;

  useEffect(() => {
    const memberName = activeMember?.member || '';
    const lat = latestPoint?.lat;
    const lon = latestPoint?.lon;
    const hasPoint = Number.isFinite(lat) && Number.isFinite(lon);

    if (!memberName || !hasPoint) {
      setMapFocus(null);
      return;
    }

    setMapFocus((previousFocus) => {
      if (previousFocus && previousFocus.member === memberName) {
        return previousFocus;
      }

      return {
        member: memberName,
        lat,
        lon,
      };
    });
  }, [activeMember?.member, latestPoint?.lat, latestPoint?.lon]);

  const recenterMapToLatest = () => {
    const memberName = activeMember?.member || '';
    const lat = latestPoint?.lat;
    const lon = latestPoint?.lon;
    if (!memberName || !Number.isFinite(lat) || !Number.isFinite(lon)) {
      return;
    }

    setMapFocus({
      member: memberName,
      lat,
      lon,
    });
  };

  const mapEmbedUrl =
    mapFocus && Number.isFinite(mapFocus.lat) && Number.isFinite(mapFocus.lon)
      ? toMapEmbedUrl(mapFocus.lat, mapFocus.lon)
      : null;

  if (!SHOW_ZELLO_SECTIONS) {
    return null;
  }

  return (
    <section id="live-locations" className="py-24 bg-black relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-1/4 w-[360px] h-[360px] bg-red-500/10 rounded-full filter blur-[140px]" />
        <div className="absolute bottom-1/3 left-1/4 w-[420px] h-[420px] bg-red-500/5 rounded-full filter blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ amount: 0.2 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-full text-sm font-medium mb-4">
            <Navigation size={14} />
            Live Locations
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Public Chat Location Map
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Live location and history for members in "{channelName}" who have location sharing enabled.
          </p>
          <p className="text-gray-500 text-sm mt-3">
            Last synced: {formatTimestamp(fetchedAt)}
          </p>
        </motion.div>

        {loadError ? (
          <div className="text-center p-10 rounded-2xl bg-gray-900/60 border border-gray-800 text-gray-300">
            Unable to retrieve live locations, please try again later.
          </div>
        ) : members.length === 0 ? (
          <div className="text-center p-10 rounded-2xl bg-gray-900/60 border border-gray-800 text-gray-300">
            No members in public chat are currently sharing location.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2 }}
              className="lg:col-span-1 rounded-2xl border border-gray-800 bg-gray-900/70 backdrop-blur-sm p-4"
            >
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Route size={16} className="text-red-400" />
                Active Members
              </h3>
              <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                {members.map((member) => {
                  const isActive = member.member === (activeMember?.member || '');
                  const latest = member.latest || null;
                  return (
                    <button
                      key={member.member}
                      onClick={() => setSelectedMember(member.member)}
                      className={`w-full text-left rounded-xl border p-3 transition ${
                        isActive
                          ? 'border-red-500/60 bg-red-500/10'
                          : 'border-gray-800 bg-black/30 hover:border-gray-700'
                      }`}
                    >
                      <p className="text-white font-medium">{member.member}</p>
                      <p className="text-gray-500 text-xs mt-1">
                        {latest?.timestampIso ? formatTimestamp(latest.timestampIso) : 'No timestamp'}
                      </p>
                      <p className="text-gray-400 text-xs mt-1">
                        {member.count || 0} point{member.count === 1 ? '' : 's'}
                      </p>
                    </button>
                  );
                })}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2 }}
              transition={{ delay: 0.05 }}
              className="lg:col-span-2 rounded-2xl border border-gray-800 bg-gray-900/70 backdrop-blur-sm p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <MapPin size={16} className="text-red-400" />
                  {activeMember?.member || 'Location'}
                </h3>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={recenterMapToLatest}
                    disabled={!latestPoint || !Number.isFinite(latestPoint?.lat) || !Number.isFinite(latestPoint?.lon)}
                    className="text-xs text-gray-300 hover:text-white disabled:text-gray-600 transition"
                  >
                    Recenter map
                  </button>
                  {latestPoint?.mapsUrl ? (
                    <a
                      href={latestPoint.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 text-sm"
                    >
                      Open in Google Maps
                      <ExternalLink size={14} />
                    </a>
                  ) : null}
                </div>
              </div>

              {mapEmbedUrl ? (
                <div className="rounded-xl overflow-hidden border border-gray-800 mb-4">
                  <iframe
                    title={`${activeMember?.member || 'Member'} live location`}
                    src={mapEmbedUrl}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-[320px] bg-black"
                  />
                </div>
              ) : (
                <div className="rounded-xl border border-gray-800 bg-black/30 p-6 text-gray-400 mb-4">
                  No map location is available for the selected member.
                </div>
              )}

              <div>
                <h4 className="text-gray-300 text-sm font-medium mb-2">Location History</h4>
                <div className="max-h-[220px] overflow-y-auto space-y-2">
                  {(activeMember?.history || []).map((point) => (
                    <div
                      key={point.id}
                      className="rounded-lg border border-gray-800 bg-black/30 px-3 py-2 flex items-center justify-between gap-3"
                    >
                      <div>
                        <p className="text-gray-200 text-sm">
                          {point.lat.toFixed(5)}, {point.lon.toFixed(5)}
                        </p>
                        <p className="text-gray-500 text-xs inline-flex items-center gap-1">
                          <Clock size={11} />
                          {formatTimestamp(point.timestampIso)}
                        </p>
                      </div>
                      <a
                        href={point.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-400 hover:text-red-300 text-xs inline-flex items-center gap-1"
                      >
                        Maps
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PublicLocationsSection;
